import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const faqs = [
  {
    question: 'How far in advance should I book equipment hire?',
    answer:
      'We recommend booking at least 4 weeks in advance for standard events and 3-6 months for weddings and large corporate events. However, we do accommodate last-minute requests based on availability.',
  },
  {
    question: 'Is delivery, setup, and collection included in the price?',
    answer:
      'Yes! All our hire prices include delivery, professional setup, and collection after your event. Our team will arrive early to ensure everything is perfectly arranged before your guests arrive.',
  },
  {
    question: 'What happens if equipment is damaged during my event?',
    answer:
      'We understand that accidents happen. Minor wear and tear is covered. For significant damage, we have a fair damage policy that we discuss during booking. We also offer optional damage waiver insurance for complete peace of mind.',
  },
  {
    question: 'Can I hire equipment without event planning services?',
    answer:
      'Absolutely! You can hire any equipment from our inventory on its own. Many clients use our hire service independently for their self-planned events. We provide full setup instructions or professional setup as needed.',
  },
  {
    question: 'Do you offer packages or discounts for large events?',
    answer:
      'Yes, we offer tiered pricing and custom packages for larger events. The more you hire, the better the rate. Contact us for a custom quote tailored to your specific needs and budget.',
  },
  {
    question: 'What areas do you service for delivery?',
    answer:
      'We service the greater metropolitan area and surrounding regions within 100km. For events further afield, we can arrange delivery for an additional transport fee. Contact us to confirm your location.',
  },
  {
    question: 'Can I visit your showroom to see the equipment in person?',
    answer:
      'Yes! We welcome showroom visits by appointment. You can see and touch all our equipment, discuss your event vision with our team, and get expert advice on the best items for your occasion.',
  },
  {
    question: 'Do you provide event styling and decoration services?',
    answer:
      'Yes, we offer full event styling as part of our planning services. This includes table styling, floral arrangements, lighting design, and complete venue transformation. We can also provide styling consultation if you prefer to DIY.',
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { ref: sectionRef, isVisible } = useScrollAnimation({
    rootMargin: '0px 0px -100px 0px',
  });
  const { ref: listRef, isVisible: listVisible } = useScrollAnimation({
    rootMargin: '0px 0px -50px 0px',
  });

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const scrollToInquiry = () => {
    const el = document.getElementById('inquiry');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="faq"
      className="py-32 bg-gradient-to-b from-white to-stone-50 relative overflow-hidden"
    >
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-amber-100/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-0 w-[400px] h-[400px] bg-amber-50/40 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="grid lg:grid-cols-5 gap-16 items-start">
          {/* Left Heading */}
          <div
            ref={sectionRef}
            className={`lg:col-span-2 lg:sticky lg:top-32 scroll-animate-left ${
              isVisible ? 'visible' : ''
            }`}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-px bg-gradient-to-r from-amber-500 to-transparent"></div>
              <span className="text-amber-600 text-xs font-semibold tracking-[0.25em] uppercase">
                Common Questions
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl font-display font-normal text-stone-900 mb-6 leading-tight">
              Everything You
              <br />
              <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-amber-400">
                Need to Know
              </span>
            </h2>

            <p className="text-stone-500 leading-relaxed text-lg mb-10">
              Got questions about our event planning services or equipment hire? We&apos;ve got answers.
            </p>

            <button
              onClick={scrollToInquiry}
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-400 text-white font-semibold rounded-full hover:from-amber-400 hover:to-amber-300 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 hover:scale-105 text-sm tracking-wide"
            >
              <i className="ri-question-answer-line text-lg"></i>
              Still Have Questions? Contact Us
            </button>

            <div className="mt-12 relative hidden lg:block">
              <div className="w-full h-64 rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/ee90f1209bd048de83892c498ac0c754.jpg"
                  alt="Equipment showroom"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/30 to-transparent"></div>
              </div>
              <div className="absolute -inset-3 border border-amber-200/40 rounded-[2rem] -z-10"></div>
            </div>
          </div>

          {/* FAQ Accordion */}
          <div
            ref={listRef}
            className={`lg:col-span-3 space-y-4 scroll-animate-right ${
              listVisible ? 'visible' : ''
            }`}
          >
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl border transition-all duration-300 ${
                  openIndex === index
                    ? 'border-amber-200 shadow-xl shadow-amber-500/5'
                    : 'border-stone-100 shadow-md hover:shadow-lg hover:border-amber-100'
                }`}
              >
                <button
                  onClick={() => toggle(index)}
                  className="w-full flex items-center justify-between gap-6 p-7 text-left cursor-pointer group"
                >
                  <div className="flex items-center gap-5">
                    <span
                      className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                        openIndex === index
                          ? 'bg-gradient-to-br from-amber-500 to-amber-400 text-white shadow-lg shadow-amber-500/20'
                          : 'bg-stone-100 text-stone-400 group-hover:bg-amber-50 group-hover:text-amber-600'
                      }`}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <h4
                      className={`font-semibold transition-colors duration-300 text-[15px] ${
                        openIndex === index ? 'text-stone-900' : 'text-stone-700 group-hover:text-stone-900'
                      }`}
                    >
                      <a href={`#faq-${index}`} className="hover:text-stone-900">
                        {faq.question}
                      </a>
                    </h4>
                  </div>
                  <div
                    className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${
                      openIndex === index
                        ? 'bg-amber-100 text-amber-600 rotate-180'
                        : 'bg-stone-50 text-stone-400 group-hover:bg-amber-50 group-hover:text-amber-500'
                    }`}
                  >
                    <i className="ri-arrow-down-s-line text-lg"></i>
                  </div>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-500 ease-in-out ${
                    openIndex === index ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-7 pb-7 pl-[5.25rem]">
                    <p className="text-stone-500 leading-relaxed text-[15px]">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}

            <div className="pt-6 text-center">
              <p className="text-stone-400 text-sm">
                Can&apos;t find your answer?{' '}
                <button
                  onClick={scrollToInquiry}
                  className="text-amber-600 font-semibold hover:text-amber-500 transition-colors cursor-pointer underline underline-offset-4 decoration-amber-300"
                >
                  Get in touch with us
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* JSON‑LD for FAQ schema – note the explicit closing tag (no self‑closing) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      ></script>
    </section>
  );
}
